import React from "react"
import { useShallow } from "zustand/react/shallow"
import { useSearchParams } from "react-router-dom"
import { debounce } from "lodash"

import { getRegistryKeys } from "../../../utils"
import { formSchemas } from "constants/authoring"

// components
import { Select } from "components-design-system"
import CommentItem from "../CommentItem/CommentItem"
import { Flex } from "antd"

// apis
import { useGetCommentTypes } from "api/hooks/admin/useCommentTypesApi"
import { useGetAuthoringComments } from "api/hooks/studies/useAuthoringApi"
import { useGetAuthoringReviewMetadata } from "api/hooks/studies/useAuthoringReviewApi"

// stores
import useAuthoringViewStore from "../../../hooks/stores/useAuthoringViewStore"
import useAuthoringStore from "../../../hooks/stores/useAuthoringStore"
import useAuthoringFieldStore from "../../../hooks/stores/useAuthoringFieldStore"
import useAuthoringCommentsStore from "../../../hooks/stores/useAuthoringCommentsStore"
import useAuthoringDataStore from "../../../hooks/stores/useAuthoringDataStore"

// styles
import "./CommentsPanel.scss"

const studyDetailsFieldKeySet = [
  "root_protocol_study_type",
  "root_protocol_protocol_id",
  "root_protocol_unapproved_new_formulation_marketed_compound",
  "root_protocol_availability_expanded_access",
  "root_protocol_is_clinical_study",
  "root_protocol_vendor",
  "root_protocol_vendor_contact",
  "root_protocol_acquired_from",
  "root_protocol_transferred_to",
  "root_protocol_proprietary_name",
  "root_protocol_established_name",
  "root_protocol_product_manufactured",
  "root_protocol_compound_code",
  "root_protocol_generic_name",
  "root_protocol_brand_name",
  "root_protocol_primary_cro",
  "root_protocol_us_fda_regulated_drug_product",
  "root_protocol_us_fda_regulated_device_product",
  "root_protocol_secondary_ids",
  "root_protocol_general",
  "root_protocol_status",
  "root_protocol_protocol_amendments",
  "root_protocol_study_details_detailed_description",
  "root_protocol_sponsoring_division",
  "root_protocol_sponsoring_unit",
  "root_protocol_ctms_study_type",
  "root_protocol_ctms_study_title",
  "root_protocol_ctms_study_status",
  "root_protocol_study_termination_decision_date",
  "root_protocol_study_termination_reason",
  "root_protocol_study_termination_date",
  "root_protocol_study_other",
  "root_protocol_complex_study_program",
  "root_protocol_same_study_design",
  "root_protocol_post_study_access",
  "root_protocol_study_extension",
  "root_protocol_plsrs_posted_on_sponsor_website_off_lslv",
  "root_protocol_plsrs_posted_on_sponsor_website_off_pcd",
  "root_protocol_public_disclosure_synopsis_posted_on_sponsor_website",
  "root_protocol_post_authorization_study_type",
  "root_protocol_disease_area_stronghold",
  "root_protocol_milestone_dates",
  "root_protocol_secondary_attributes",
]

const registriesSet = new Set(["ctg", "eudra", "eudract", "eupas", "jrct", "ctis"])

export default function CommentsPanel() {
  const [searchParams] = useSearchParams()
  const projId = React.useMemo(() => {
    return searchParams.get("proj-id")
  }, [])
  const taskId = React.useMemo(() => {
    return searchParams.get("task-id")
  }, [])

  // local state
  const { isReviewerView, formView, formSection, formStatus } = useAuthoringViewStore(
    useShallow((state) => ({
      isReviewerView: state.isReviewerView,
      formView: state.formView,
      formSection: state.formSection,
      formStatus: state.formStatus,
    }))
  )

  const { openComment, setOpenComment } = useAuthoringCommentsStore(
    useShallow((state) => ({
      openComment: state.openComment,
      setOpenComment: state.setOpenComment,
    }))
  )

  const [filters, setFilters] = React.useState([])
  const [filteredComments, setFilteredComments] = React.useState([])

  // server state
  const authoringId = useAuthoringStore((state) => state.authoringId)
  const { data: comments = [] } = useGetAuthoringComments(authoringId)
  const { data: commentTypes = [] } = useGetCommentTypes()
  const { data: authReviewData } = useGetAuthoringReviewMetadata(
    isReviewerView === true ? authoringId : null,
    isReviewerView === true ? { "project-id": projId, "actionitem-id": taskId } : {}
  )

  const { forkedStudyData, overrideData } = useAuthoringDataStore(
    useShallow((state) => ({
      forkedStudyData: state.forkedStudyData,
      overrideData: state.overrideData,
    }))
  )

  const [refreshComments, setRefreshComments] = React.useState(false)

  const studyDetailsFilter = (target) => {
    for (let i = 0; i < studyDetailsFieldKeySet.length; i++) {
      if (target.startsWith(studyDetailsFieldKeySet[i])) {
        return true
      }
    }
    return false
  }

  const getCurrSectionComments = (comments) => {
    if (formSection && Array.isArray(comments)) {
      const forkedCommentsSet = new Set()

      let filtered = comments.filter((comment) => {
        // check if form view and form section exists
        if (comment.form_section && comment.form_view && formView?.id !== "global") {
          // if form section and view matches then render the comment
          if (comment.form_section === formSection.id && comment.form_view === formView?.id) {
            return true
          } else {
            return false
          }
        }
        if (formView?.id !== "global") {
          let forkRegArray = comment.field_key.split("_")
          const forkReg = forkRegArray[forkRegArray.length - 1]

          if (registriesSet.has(forkReg)) {
            if (forkReg === formView?.id || (forkReg === "eudract" && formView?.id === "eudra")) {
              forkRegArray.pop()
              const originalJpath = forkRegArray.join("_")
              forkedCommentsSet.add(originalJpath)
            } else {
              forkedCommentsSet.add(comment.field_key)
            }
          }
        }

        if (
          (comment.field_key.startsWith("root_protocol") || comment.field_key.startsWith("st-")) &&
          formSection.id === "protocol"
        ) {
          return true
        } else if (
          comment.field_key.startsWith("root_sponsor_trial_website") &&
          formSection.id === "sponsorTrialWebsite"
        ) {
          return true
        } else if (
          comment.field_key.startsWith("root_delayed_results_certification") &&
          formSection.id === "delayedResultsCertification"
        ) {
          return true
        } else if (
          formSection?.id === "results" &&
          !comment.field_key.startsWith("root_protocol") &&
          !comment.field_key.startsWith("root_sponsor_trial_website") &&
          !comment.field_key.startsWith("root_delayed_results_certification") &&
          !comment.field_key.startsWith("st-")
        ) {
          return true
        } else if (formSection?.id === "secondaryAttributes" && studyDetailsFilter(comment.field_key)) {
          return true
        }
        return false
      })

      return filtered.filter((comment) => {
        if (forkedCommentsSet.has(comment.field_key)) {
          return false
        }
        return true
      })
    }
    return []
  }

  const getSortedComments = (comments) => {
    const sortByDomPosition = (arr) => {
      let returnArr = [...arr]
      return returnArr.sort((a, b) => {
        let aField = document.getElementById(a.field_key)
        let bField = document.getElementById(b.field_key)
        if (aField && bField) {
          if (aField.compareDocumentPosition(bField) & Node.DOCUMENT_POSITION_FOLLOWING) {
            return -1
          } else {
            return 1
          }
        } else if (aField) {
          return -1
        } else if (bField) {
          return -1
        }

        return 0
      })
    }

    // TODO: make into pure function, returning a new array
    const sortBySystemDateIfSameFieldKey = (arr) => {
      // Create an object to store items with the same "field_key"
      const fieldKeyMap = {}

      // Iterate through the array and group items by "field_key"
      arr.forEach((item) => {
        const fieldKey = item.field_key
        if (!fieldKeyMap[fieldKey]) {
          fieldKeyMap[fieldKey] = []
        }
        fieldKeyMap[fieldKey].push(item)
      })

      // Sort each group by "system_date" in descending order (recent dates first)
      for (const fieldKey in fieldKeyMap) {
        if (fieldKeyMap.hasOwnProperty(fieldKey)) {
          fieldKeyMap[fieldKey].sort((a, b) => b.system_date.localeCompare(a.system_date))
        }
      }

      // Rebuild the sorted array
      const sortedArray = []
      for (const fieldKey in fieldKeyMap) {
        if (fieldKeyMap.hasOwnProperty(fieldKey)) {
          sortedArray.push(...fieldKeyMap[fieldKey])
        }
      }

      // Update the original array in place
      arr.length = 0
      arr.push(...sortedArray)
    }

    const sortedByDom = sortByDomPosition([...comments])
    sortBySystemDateIfSameFieldKey(sortedByDom)
    sortedByDom.sort((a, b) => (a.favorite === b.favorite ? 0 : a.favorite ? -1 : 1))
    return sortedByDom
  }

  const { hiddenFields } = useAuthoringFieldStore(
    useShallow((state) => ({
      hiddenFields: state.hiddenFields,
    }))
  )

  const refreshCommentsDebounce = debounce(() => setRefreshComments(!refreshComments), 500)

  // update comments on load or when user chooses filter
  React.useEffect(() => {
    if (Array.isArray(comments) && Array.isArray(commentTypes)) {
      let filtered = [...comments]

      if (formSection?.id === "protocol") {
        filtered = filtered.filter((comment) => {
          const regKeys = getRegistryKeys({
            formSection,
            formSchemas,
            hiddenFields,
            jpath: comment.field_key,
          })

          if (formView?.id === "global" || regKeys.find((reg) => reg === formView?.id)) {
            return true
          }

          return false
        })
      }

      // for reviewer view, show only comment types in authoring-review-metadata
      if (authReviewData && isReviewerView && Array.isArray(commentTypes)) {
        const commentTags = authReviewData.comment_types.map(
          (typeId) => commentTypes.find((type) => type.id === typeId)?.name
        )
        filtered = filtered.filter((comment) => comment.tags.some((tag) => commentTags.includes(tag)))
      }

      if (filters.length === 0) {
        // when no filters chosen, show all types, except for "resolved"
        filtered = filtered.filter((comment) => !comment.resolved)
      } else {
        filtered = filtered.filter((comment) => {
          // if "Resolved" filter, show all resolved
          if (filters.some((option) => option.label === "Resolved") && comment.resolved) return true
          // if other category, show those
          if (filters.some((option) => !comment.resolved && comment.tags.find((tag) => tag === option.label)))
            return true
          return false
        })
      }
      // sort comments and only show comments for current section (getCurrSectionComments depends on formSection)
      const finalFiltered = getSortedComments(getCurrSectionComments(filtered))
      setFilteredComments(finalFiltered)
    }
  }, [refreshComments, formSection, formView])

  React.useEffect(() => {
    if (openComment) {
      setOpenComment(null)
    }
  }, [formSection, formView])

  React.useEffect(() => {
    refreshCommentsDebounce()
  }, [
    comments,
    authReviewData,
    isReviewerView,
    commentTypes,
    filters,
    formSection,
    formView,
    formStatus,
    JSON.stringify(forkedStudyData),
    JSON.stringify(overrideData),
  ])

  return (
    <Flex
      className={`comments-panel ${isReviewerView ? "comments-panel-reviewer " : ""}`}
      vertical={true}
      gap={16}
    >
      {isReviewerView !== true && (
        <Flex
          className="filter-container"
          wrap
          justify="center"
        >
          <Select
            mode="multiple"
            placeholder="Filter comments by tags"
            options={Array.isArray(commentTypes) ? [{ id: 999, name: "Resolved" }, ...commentTypes] : []}
            onChange={(_, option) => setFilters(option)}
            allowClear
            className="comments-panel-filter-select"
          />
        </Flex>
      )}
      <div className="comments-container">
        {filteredComments &&
          filteredComments.map((comment) => {
            return (
              <CommentItem
                key={comment.id}
                authoringId={authoringId}
                comment={comment}
                isReviewerView={isReviewerView}
              />
            )
          })}
      </div>
    </Flex>
  )
}
